import { Button, Input } from '@bbl-digital/snorre'
import { QuillEditor, useIsMobile } from '@bbl-digital/snorre-v2'
import { useFormikContext } from 'formik'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import ErrorText from 'shared/components/ErrorText'
import { IEmailMessage, SendEmailFieldName } from 'shared/models/Message'
import { SEND_EMAIL_UPDATE_DRAFT_REQUESTED } from 'shared/store/sendMessage/email/actionTypes'
import {
  getSendEmail,
  getSendEmailRecipients,
} from 'shared/store/sendMessage/email/selectors'
import SendToRecipients from '../../SendToRecipients'
import SendEmailAttachments from '../SendEmailAttachments'
import { ActionButtons, BottomWrapper, ScrollWrapper } from '../styles'

// Define the structure for queued changes
interface IProps {
  isFullscreen: boolean
  handleClose: () => void
}
interface QueuedChange {
  field: SendEmailFieldName
  value: string
}
const DEBOUNCE_TIME = 1000
const SendEmailForm: FC<IProps> = ({ isFullscreen, handleClose }) => {
  const isMobile = useIsMobile()
  const dispatch = useDispatch()

  const { values, setFieldValue, submitForm, touched, errors } =
    useFormikContext<IEmailMessage>()

  const emailRecipients = useSelector(getSendEmailRecipients)
  const { messageId, processing, error } = useSelector(getSendEmail)

  const [savingAttachments, setSavingAttachments] = useState(false)

  // State to store changes that occur before messageId is available
  const [changeQueue, setChangeQueue] = useState<QueuedChange[]>([])

  // Ref to store the current debounce timer
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Function to handle debounced dispatching of changes
  const debouncedDispatch = useCallback(
    (field: SendEmailFieldName, value: string) => {
      // Clear any existing timer to reset the debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      // Set a new timer
      debounceTimerRef.current = setTimeout(() => {
        if (messageId) {
          // If messageId is available, dispatch the update action
          dispatch({
            type: SEND_EMAIL_UPDATE_DRAFT_REQUESTED,
            payload: { field, value, id: messageId },
          })
        } else {
          // If messageId is not available, queue the change
          setChangeQueue((prevQueue) => [...prevQueue, { field, value }])
        }
      }, DEBOUNCE_TIME)
    },
    [dispatch, messageId]
  )

  const handleInputChange = useCallback((value: string) => {
    setFieldValue('title', value)
    debouncedDispatch('title', value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleEditorChange = useCallback((value: string) => {
    setFieldValue('contentHtml', value)
    debouncedDispatch('contentHtml', value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleEditorBlur = () => {
    document.getElementById('wrapper')?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Effect to process queued changes when messageId becomes available
  useEffect(() => {
    if (messageId && changeQueue.length > 0) {
      // Process the queue immediately when messageId becomes available
      changeQueue.forEach(({ field, value }) => {
        dispatch({
          type: SEND_EMAIL_UPDATE_DRAFT_REQUESTED,
          payload: { field, value, id: messageId },
        })
      })
      // Clear the queue after processing
      setChangeQueue([])
    }
  }, [messageId, changeQueue, dispatch])

  useEffect(() => {
    // Cleanup function to clear any pending debounce timer
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return (
    <>
      <ScrollWrapper $isFullscreen={isFullscreen} id="wrapper">
        <SendToRecipients />
        <Input
          label="Emne"
          value={values.title}
          onChange={(e) => handleInputChange(e.target.value)}
          maxlength={2048}
          invalidMessage={(touched.title && errors.title) || undefined}
          validation
        />

        <QuillEditor
          initialValue={values.contentHtml}
          withSoftBreakStyle={false}
          onChange={handleEditorChange}
          onBlur={handleEditorBlur}
          maxHeight={isMobile ? '400px' : '500px'}
          type="rich"
          invalidMessage={
            (touched.contentHtml && errors.contentHtml) || undefined
          }
        />
      </ScrollWrapper>

      <BottomWrapper $isMobile={isMobile}>
        <SendEmailAttachments
          savingAttachments={savingAttachments}
          onSaving={(e) => setSavingAttachments(e)}
        />

        <ActionButtons $isMobile={isMobile}>
          <Button
            onClick={handleClose}
            outline
            disabled={processing || savingAttachments}
            trackingName="Kommunikasjon"
            trackingEvent="E-post: Lagre utkast"
          >
            Lagre utkast
          </Button>
          <Button
            type="submit"
            onClick={submitForm}
            disabled={savingAttachments || !emailRecipients.length}
            loading={processing}
            trackingName="Kommunikasjon"
            trackingEvent="E-post: Send e-post"
          >
            Send
          </Button>
        </ActionButtons>
      </BottomWrapper>

      {error && <ErrorText withIcon={false} text={error} />}
    </>
  )
}

export default SendEmailForm
